import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { concat, Observable, of as observableOf, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { AbstractOrderApiService } from 'src/app/core/services/abstract/abstract-order-api-service';
import * as actions from './actions';

@Injectable()
export class OrderStoreEffects {
  private orderApiService = inject(AbstractOrderApiService);
  private actions$ = inject(Actions);

  loadRequestEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.LoadRequestAction>(actions.ActionTypes.LOAD_REQUEST),
      switchMap(action =>
        this.orderApiService.getOrders(action.userId, action.fromDate, action.toDate).pipe(
          map(items => (items.isSucceeded ? new actions.LoadSuccessAction(items.result) : new actions.FailureAction(items.errors[0]))),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  loadByIdRequestEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.LoadByIdRequestAction>(actions.ActionTypes.LOAD_BY_ID_REQUEST),
      switchMap(action =>
        this.orderApiService.getOrderById(action.orderId, action.userId).pipe(
          map(item => (item.isSucceeded ? new actions.LoadByIdSuccessAction(item.result) : new actions.FailureAction(item.errors[0]))),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  setSelectedRequestEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.SetSelectedRequestAction>(actions.ActionTypes.SET_SELECTED_REQUEST),
      switchMap(action =>
        this.orderApiService.getOrderById(action.orderId, action.userId).pipe(
          map(items =>
            items.isSucceeded ? new actions.SetSelectedSuccessAction(items.result) : new actions.FailureAction(items.errors[0])
          ),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  setSelectedEmployeeIdRequestEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.SetSelectedEmployeeIdRequestAction>(actions.ActionTypes.SET_SELECTED_EMPLOYEE_ID_REQUEST),
      switchMap(action => observableOf(new actions.SetSelectedEmployeeIdSuccessAction(action.employeeId)))
    )
  );

  createRequestEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.CreateRequestAction>(actions.ActionTypes.CREATE_REQUEST),
      switchMap(action =>
        this.orderApiService.createOrder(action.order, action.files).pipe(
          map(items => (items.isSucceeded ? new actions.CreateSuccessAction(items.result) : new actions.FailureAction(items.errors[0]))),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  saveRequestEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.SaveRequestAction>(actions.ActionTypes.SAVE_REQUEST),
      switchMap(action =>
        this.orderApiService
          .saveOrder(action.order, action.isChangedOrder, action.userId, action.files)

          .pipe(
            map(items => (items.isSucceeded ? new actions.CreateSuccessAction(items.result) : new actions.FailureAction(items.errors[0]))),
            catchError(error => observableOf(new actions.FailureAction(error)))
          )
      )
    )
  );

  loadTemplateRequestEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.LoadTemplateRequestAction>(actions.ActionTypes.LOAD_TEMPLATE_REQUEST),
      switchMap(action =>
        this.orderApiService.getTemplate(action.categoryId, action.serviceId).pipe(
          map(items =>
            items.isSucceeded ? new actions.LoadTemplateSuccessAction(items.result) : new actions.FailureAction(items.errors[0])
          ),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  saveStatusRequestEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.SaveStatusRequestAction>(actions.ActionTypes.SAVE_STATUS_REQUEST),
      switchMap(action =>
        this.orderApiService.saveStatus(action.orderId, action.mark, action.userId, action.comment, action.files, action.newStatusId).pipe(
          mergeMap(item =>
            item.isSucceeded
              ? [
                  new actions.SaveStatusSuccessAction(item.result, action.setSelected),
                  new actions.LoadActsRequestAction(item.result.id),
                  new actions.LoadAllowedStatusesRequestAction(item.result.id, action.userId),
                  new actions.LoadForNotificationRequestAction(action.userId),
                ]
              : [new actions.FailureAction(item.errors[0])]
          ),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  loadActsRequestEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.LoadByIdRequestAction>(actions.ActionTypes.LOAD_ACTS_REQUEST),
      switchMap(action =>
        this.orderApiService.getActs(action.orderId).pipe(
          map(items => (items.isSucceeded ? new actions.LoadActsSuccessAction(items.result) : new actions.FailureAction(items.errors[0]))),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  loadAllowedStatusesRequestEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.LoadAllowedStatusesRequestAction>(actions.ActionTypes.LOAD_ALLOWED_STATUSES_REQUEST),
      switchMap(action =>
        this.orderApiService.getAllowedStatuses(action.orderId, action.userId).pipe(
          map(items =>
            items.isSucceeded ? new actions.LoadAllowedStatusesSuccessAction(items.result) : new actions.FailureAction(items.errors[0])
          ),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  createActRequestEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.CreateActRequestAction>(actions.ActionTypes.CREATE_ACT_REQUEST),
      switchMap(action =>
        this.orderApiService.createAct(action.orderId, action.userId, action.value).pipe(
          map(items =>
            items.isSucceeded ? new actions.LoadActsRequestAction(items.result.requestId) : new actions.FailureAction(items.errors[0])
          ),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  LoadForNotificationRequestEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.LoadForNotificationRequestAction>(actions.ActionTypes.LOAD_FOR_NOTIFICATION_REQUEST),
      switchMap(action =>
        this.orderApiService.getOrdersForNotification(action.userId).pipe(
          map(item =>
            item.isSucceeded ? new actions.LoadForNotificationSuccessAction(item.result) : new actions.FailureAction(item.errors[0])
          ),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );
}
